import React, { useState } from 'react'
import AdminCard from './adminCard'
import InputTypes from '../constants/editabletypes.constants'
import { Button } from '@material-ui/core'
import FreetypeDialog from '../components/editableDialogs/freetypeDialog'
import DatePickerDialog from './editableDialogs/datePickerDialog'
import TimePicker from '../components/editableDialogs/timePickerDialogAccessible'
import DropdownDialog from '../components/editableDialogs/dropdownDialog'
import HtmlDialog from '../components/editableDialogs/htmlDialog'
import CarouselDialog from './editableDialogs/carouselDialog'
import { useService } from '../contexts/serviceContext'
import PropTypes from 'prop-types'
import { dateTimeConstants } from '../constants/datetime.constants'

const generateCarouselOptions = editableRegionOptions => {
    let result = []
    let tempId = 1

    editableRegionOptions.forEach(ero => {
        if (!ero.isArchived) {
            result.push({
                id: ero.id,
                value: ero.value,
                description: ero.description,
                isEditableByUser: ero.isEditableByUser,
            })
            tempId = tempId + 1
        }
    })
    return result
}

const EditableRegionFormPreview = ({ editableRegion, prePopulatedState }) => {
    const [showEditableDialog, setShowEditableDialog] = useState(false)
    const { serviceConfig } = useService()
    const [
        selectedOptionCarouselDialog,
        setSelectedOptionCarouselDialog,
    ] = useState(editableRegion.editableRegionOptions[0]?.id ?? 1)
    const [indexCarouselDialog, setIndexCarouselDialog] = useState(0)
    const timeFormat =
        editableRegion?.inputCustomFormat ??
        dateTimeConstants.DEFAULT_TIME_FORMAT
    const dateFormat =
        editableRegion?.inputCustomFormat ??
        dateTimeConstants.DEFAULT_DATE_FORMAT

    const carouselChanged = (e, id, html) => {
        const index = editableRegion.editableRegionOptions.findIndex(
            item => item.id == id
        )
        setSelectedOptionCarouselDialog(id)
        setIndexCarouselDialog(index)
    }

    // since it is a preview, there is no need to have onChange() method
    const noop = () => {}

    const PreviewComponent = () => {
        switch (editableRegion.inputTypeId) {
            case InputTypes.FreeText:
                return (
                    <FreetypeDialog
                        title={editableRegion.name}
                        description={editableRegion.description}
                        open={showEditableDialog}
                        onConfirm={() => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentValue={`Example text for ${editableRegion.name}`}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                    />
                )
            case InputTypes.DatePicker:
                return (
                    <DatePickerDialog
                        title={editableRegion.name}
                        description={editableRegion.description}
                        open={showEditableDialog}
                        onConfirm={() => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentDate={new Date()}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                        dateFormat={dateFormat}
                    />
                )
            case InputTypes.TimePicker:
                return (
                    <TimePicker
                        title={editableRegion.name}
                        description={editableRegion.description}
                        open={showEditableDialog}
                        onConfirm={() => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentTime={new Date()}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                        timeFormat={timeFormat}
                    />
                )
            case InputTypes.Dropdown:
                return (
                    <DropdownDialog
                        title={editableRegion.name}
                        description={editableRegion.description}
                        open={showEditableDialog}
                        onConfirm={() => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentValue=''
                        dropdownItems={editableRegion.editableRegionOptions}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                    />
                )
            case InputTypes.HtmlEditor:
                return (
                    <HtmlDialog
                        title={editableRegion.name}
                        description={editableRegion.description}
                        open={showEditableDialog}
                        onConfirm={state => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentHtml={
                            prePopulatedState?.length > 0
                                ? prePopulatedState
                                : '<p>This is some sample text</p>'
                        }
                        fontSize={serviceConfig.fontSize}
                        fontFamily={serviceConfig.fontFamily}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                    />
                )
            case InputTypes.Carousel:
                return (
                    <CarouselDialog
                        title={editableRegion.name}
                        open={showEditableDialog}
                        description={editableRegion.description}
                        onConfirm={state => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentIndex={selectedOptionCarouselDialog}
                        carouselOptions={generateCarouselOptions(
                            editableRegion.editableRegionOptions
                        )}
                        fontSize={serviceConfig.fontSize}
                        fontFamily={serviceConfig.fontFamily}
                        currentHtmlText={
                            editableRegion.editableRegionOptions[
                                indexCarouselDialog
                            ].value ?? ''
                        }
                        onChange={(e, id, html) => {
                            carouselChanged(e, id, html)
                        }}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                    />
                )
            case InputTypes.Address:
                return (
                    <FreetypeDialog
                        title={editableRegion.name}
                        open={showEditableDialog}
                        onConfirm={() => setShowEditableDialog(false)}
                        onReset={() => setShowEditableDialog(false)}
                        onClose={() => setShowEditableDialog(false)}
                        currentValue={`Example text for ${editableRegion.name}`}
                        resetButtonVisible={false}
                        confirmButtonText={'Close'}
                        onChange={noop}
                    />
                )
            default:
                throw new Error('Editable using unrecognised input type id')
        }
    }

    return (
        <AdminCard title={'Preview'}>
            <Button
                variant='outlined'
                onClick={() => setShowEditableDialog(true)}
            >
                Open Dialog
            </Button>
            <PreviewComponent />
        </AdminCard>
    )
}
EditableRegionFormPreview.propTypes = {
    editableRegion: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        inputTypeId: PropTypes.number.isRequired,
        editableRegionOptions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                    .isRequired,
                value: PropTypes.string.isRequired,
            })
        ),
    }).isRequired,
    serviceConfig: PropTypes.shape({
        fontSize: PropTypes.string.isRequired,
        fontFamily: PropTypes.string.isRequired,
    }).isRequired,
    prePopulatedState: PropTypes.string.isRequired,
}

export default EditableRegionFormPreview
